import {
  addProviderInstitution,
  getProviderMe,
  updateProviderInstitution,
  updateProviderMe,
} from "@/app/api/provider";
import {
  AddInstitutionDTO,
  ProviderDTO,
  ProviderInstitutionDTO,
  UpdateProviderDTO,
  UpdateProviderInstitutionDTO,
} from "@/app/api/provider/types";
import { ErrorResponse } from "@/app/types/error";
import { ELocalStorageKeys } from "@/app/types/localStorage";
import { setItemSessionStorage } from "@/lib/utils";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

interface ProviderState {
  data: ProviderDTO;
  institutionData: ProviderInstitutionDTO;
  updateProvider: UpdateProviderDTO;
  updateInstitution: UpdateProviderInstitutionDTO;
  addInstitution: AddInstitutionDTO;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
}

const initialState: ProviderState = {
  data: {} as ProviderDTO,
  institutionData: {} as ProviderInstitutionDTO,
  updateProvider: {} as UpdateProviderDTO,
  updateInstitution: {} as UpdateProviderInstitutionDTO,
  addInstitution: {} as AddInstitutionDTO,
  status: "idle",
  error: null,
};

export const getProvider = createAsyncThunk(
  "provider/me",
  async (_, { rejectWithValue }) => {
    try {
      const response = await getProviderMe();

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      if (
        typeof response === "object" &&
        "status" in response &&
        response.status === "success"
      ) {
        setItemSessionStorage(
          ELocalStorageKeys.ACCOUNT_STATUS,
          response.data.active === true ? "active" : "disabled"
        );
      }

      return response as { data: ProviderDTO };
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const updateProvider = createAsyncThunk(
  "provider/me/update",
  async (data: UpdateProviderDTO, { rejectWithValue }) => {
    try {
      const response = await updateProviderMe(data);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response as { data: ProviderDTO };
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const updateInstitution = createAsyncThunk(
  "provider/institution/update",
  async (
    { id, data }: { id: string; data: UpdateProviderInstitutionDTO },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateProviderInstitution(id, data);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response as { data: ProviderInstitutionDTO };
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

export const addInstitution = createAsyncThunk(
  "provider/institution/add",
  async (data: AddInstitutionDTO, { rejectWithValue }) => {
    try {
      const response = await addProviderInstitution(data);

      if (
        response &&
        typeof response === "object" &&
        "status" in response &&
        response.status === "fail"
      ) {
        return rejectWithValue((response as ErrorResponse).message);
      }

      return response as { data: ProviderDTO };
    } catch (error) {
      if (error instanceof Error && error.message) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue("An unknown error occurred");
    }
  }
);

const providerSlice = createSlice({
  name: "provider",
  initialState,
  reducers: {
    updateProviderState: (state, action) => {
      state.updateProvider = {
        ...state.updateProvider,
        ...action.payload,
      };
    },
    updateInstitutionState: (state, action) => {
      state.updateInstitution = {
        ...state.updateInstitution,
        ...action.payload,
      };
    },
    updateAddInstitutionState: (state, action) => {
      state.addInstitution = {
        ...state.addInstitution,
        ...action.payload,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getProvider.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getProvider.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.data = action.payload.data;
      })
      .addCase(getProvider.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(updateProvider.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateProvider.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.data = action.payload.data;
      })
      .addCase(updateProvider.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(updateInstitution.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(updateInstitution.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.institutionData = action.payload.data;
      })
      .addCase(updateInstitution.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      })
      .addCase(addInstitution.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(addInstitution.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.error = null;
        state.data = action.payload.data;
      })
      .addCase(addInstitution.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message || null;
      });
  },
});

export const {
  updateAddInstitutionState,
  updateInstitutionState,
  updateProviderState,
} = providerSlice.actions;

export const providerReducer = providerSlice.reducer;
